import { useParams, Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import games from "../../data/games";
import PackageSelector from "./PackageSelector";
import {
  ChevronLeft,
  Clock,
  Target,
  Users,
  ShieldCheck,
  Cpu,
  Monitor,
  Wifi,
  AlertTriangle,
} from "lucide-react";

function DimensionBar({ label, value, color, delay = 0 }) {
  const [animated, setAnimated] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setAnimated(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div ref={ref} className="space-y-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-700 font-medium">{label}</span>
        <span className="font-semibold tabular-nums" style={{ color }}>{value}%</span>
      </div>
      <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700 ease-out"
          style={{
            width: animated ? `${value}%` : "0%",
            background: color,
            transitionDelay: `${delay}ms`,
            boxShadow: `0 0 8px ${color}55`,
          }}
        />
      </div>
    </div>
  );
}

function DifficultyPill({ difficulty }) {
  const map = {
    Facile: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Intermédiaire: "bg-amber-50 text-amber-700 border-amber-200",
    Avancé: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium ${map[difficulty] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
      <Target className="h-3.5 w-3.5" />
      {difficulty}
    </span>
  );
}

function Product() {
  const { id } = useParams();
  const game = games.find((g) => g.id.toString() === id);

  if (!game) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-sm text-slate-600">Jeu introuvable.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">

      {/* Breadcrumb */}
      <div className="relative overflow-hidden border-b border-slate-200 bg-white">
        <div className="pointer-events-none absolute inset-0 opacity-60" style={{ background: "radial-gradient(circle at 15% 0%, rgba(99,102,241,0.12) 0, transparent 55%)" }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex items-center justify-between gap-3">
            <div className="text-xs text-slate-500 flex items-center gap-2">
              <Link to="/catalogue" className="inline-flex items-center gap-1.5 hover:text-indigo-600 transition-colors">
                <ChevronLeft className="h-4 w-4" />Catalogue
              </Link>
              <span className="text-slate-300">/</span>
              <span className="text-slate-700 line-clamp-1">{game.name}</span>
            </div>
            <div className="hidden sm:flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-700">
                <ShieldCheck className="h-3.5 w-3.5 text-indigo-600" />Paiement sécurisé
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Hero immersif sombre */}
      <div className="relative overflow-hidden bg-slate-900">
        <img src={game.image} alt={game.name} className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="pointer-events-none absolute inset-0 opacity-[0.035]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")", backgroundSize: "120px" }} />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-slate-900/50 via-slate-900/30 to-slate-900" />
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse at 20% 50%, rgba(99,102,241,0.14) 0, transparent 60%), radial-gradient(ellipse at 80% 20%, rgba(249,115,22,0.09) 0, transparent 50%)" }} />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="max-w-2xl">
            <div className="flex flex-wrap items-center gap-2 mb-5">
              {game.tags?.map(tag => (
                <span key={tag} className="inline-flex items-center rounded-full bg-white/10 border border-white/20 backdrop-blur px-3 py-1 text-[11px] font-medium text-white/80">
                  # {tag}
                </span>
              ))}
              {game.isAI && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-400/10 border border-amber-400/30 px-3 py-1 text-[11px] font-medium text-amber-300">
                  <Cpu className="h-3 w-3" />Visuels IA
                </span>
              )}
            </div>

            <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-4 leading-tight">
              {game.name}
            </h1>

            <p className="text-base sm:text-lg text-slate-300 leading-relaxed mb-6 max-w-xl">
              Une fuite interne. Une IA volée. 90 minutes pour remonter la piste avant que tout s'effondre.
            </p>

            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-3 py-1.5 text-sm text-white/90 backdrop-blur">
                <Clock className="h-4 w-4 text-indigo-300" />{game.duration}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-3 py-1.5 text-sm text-white/90 backdrop-blur">
                <Users className="h-4 w-4 text-orange-300" />{game.players}
              </span>
              <DifficultyPill difficulty={game.difficulty} />
              <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-800 border border-white/10 px-3 py-1.5 text-sm text-white/90">
                <Monitor className="h-4 w-4 text-orange-300" />Ordinateur requis
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Corps principal */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,3fr)_minmax(0,2.2fr)] items-start">

          {/* Colonne gauche */}
          <div className="space-y-6">

            {/* Bande scénario */}
            <div className="relative overflow-hidden rounded-2xl bg-slate-900 border border-slate-700/60">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-indigo-500/60 to-transparent" />
              <div className="p-6 sm:p-7">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-[10px] font-bold tracking-[0.25em] text-indigo-400 uppercase">Scénario</span>
                  <div className="flex-1 h-px bg-indigo-500/20" />
                  <span className="text-[10px] text-slate-500 font-mono">CLASSIFICATION : INTERNE</span>
                </div>
                <div className="space-y-3 text-sm text-slate-300 leading-relaxed">
                  <p className="text-slate-100 font-medium">
                    Vous êtes employés d'<span className="text-indigo-300">Origami Corp</span>. Votre IA phare, <span className="text-indigo-300">Origami</span>, devait révolutionner l'empathie artificielle.
                  </p>
                  <p>
                    À 90 minutes de la conférence de presse, le concurrent <span className="text-orange-300 font-medium">PaperFold</span> annonce un produit identique au vôtre, jusqu'au moindre détail. Une fuite. Interne. Quelqu'un parmi vous.
                  </p>
                  <p>
                    Dispersés dans les locaux, chacun avec ses accès et ses indices, vous devez <span className="text-white font-medium">reconstruire la chaîne des événements</span> avant que la conférence commence. Et trouver la source, qui n'est peut-être pas celle que vous imaginez...
                  </p>
                </div>
                <div className="mt-5 pt-4 border-t border-slate-700/50 flex items-center gap-3">
                  <span className="text-[11px] text-slate-500 font-mono">PROTOCOLE D'URGENCE ACTIVÉ</span>
                  <div className="flex gap-1">
                    {[0, 150, 300].map(d => (
                      <span key={d} className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse" style={{ animationDelay: `${d}ms` }} />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Pilliers */}
            <div>
              <h2 className="text-sm font-semibold text-slate-500 mb-3 flex items-center gap-3 uppercase tracking-wider text-[11px]">
                <span className="h-px flex-1 bg-slate-200" />
                Ce que vos équipes vont vivre
                <span className="h-px flex-1 bg-slate-200" />
              </h2>
              <div className="grid sm:grid-cols-3 gap-3">
                {(game.pillars || []).map((p, i) => (
                  <div key={i} className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm hover:shadow-md hover:border-indigo-200 transition-all group">
                    <div className="text-2xl mb-2">{p.icon}</div>
                    <h3 className="text-sm font-semibold text-slate-900 mb-1 group-hover:text-indigo-700 transition-colors">{p.title}</h3>
                    <p className="text-xs text-slate-500 leading-relaxed">{p.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* ADN jauges */}
            {game.dimensions?.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                <h2 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-indigo-500" />
                  ADN de l'expérience
                </h2>
                <div className="space-y-4">
                  {game.dimensions.map((d, i) => (
                    <DimensionBar key={i} label={d.label} value={d.value} color={d.color} delay={i * 120} />
                  ))}
                </div>
              </div>
            )}

            {/* Pré-requis */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Monitor className="h-4 w-4 text-slate-400" />
                Pré-requis techniques
              </h2>
              <ul className="space-y-2.5">
                <li className="flex items-start gap-2.5 text-sm text-slate-600">
                  <Monitor className="h-4 w-4 shrink-0 mt-0.5 text-slate-400" />
                  <span><span className="font-medium text-slate-800">Ordinateur (desktop ou laptop)</span> avec navigateur récent. Non compatible mobile ni tablette.</span>
                </li>
                <li className="flex items-start gap-2.5 text-sm text-slate-600">
                  <Wifi className="h-4 w-4 shrink-0 mt-0.5 text-slate-400" />
                  <span>Connexion internet stable recommandée.</span>
                </li>
                <li className="flex items-start gap-2.5 text-sm text-slate-600">
                  <Users className="h-4 w-4 shrink-0 mt-0.5 text-slate-400" />
                  <span><span className="font-medium text-slate-800">Outil de visio externe</span> pour communiquer entre joueurs (Teams, Meet, Zoom…).</span>
                </li>
              </ul>
            </div>

            {/* Mention IA */}
            {game.isAI && (
              <div className="flex items-start gap-2 rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-[12px] text-amber-800">
                <Cpu className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
                <p>Les visuels de cette expérience sont créés à l'aide d'outils d'intelligence artificielle, puis retravaillés et validés par Blackout Games.</p>
              </div>
            )}

          </div>

          {/* Colonne droite sticky */}
          <div className="lg:sticky lg:top-20 flex flex-col gap-4">

            <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
              <h2 className="text-sm font-semibold text-slate-900 mb-3">En bref</h2>
              <ul className="text-sm text-slate-600 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-indigo-500 mt-0.5 shrink-0">•</span>
                  <span><span className="font-medium text-slate-800">4 à 6 joueurs</span> par session. Présentiel, distanciel ou hybride.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-500 mt-0.5 shrink-0">•</span>
                  <span>Mise en place en <span className="font-medium text-slate-800">5 minutes</span> côté organisateur.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-500 mt-0.5 shrink-0">•</span>
                  <span>Aucun logiciel à installer, 100 % navigateur.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-500 mt-0.5 shrink-0">•</span>
                  <span>Dashboard organisateur pour piloter les sessions en autonomie.</span>
                </li>
                <li className="flex items-start gap-2">
                  <AlertTriangle className="h-3.5 w-3.5 text-orange-500 mt-0.5 shrink-0" />
                  <span className="text-orange-700 font-medium">Ordinateur requis. Non compatible mobile / tablette.</span>
                </li>
              </ul>
            </div>

            <div>
              <h2 className="text-sm font-semibold text-slate-900 mb-2">Choisissez votre pack</h2>
              <div className="relative">
                <div className="pointer-events-none absolute -inset-0.5 rounded-2xl opacity-70" style={{ background: "radial-gradient(circle at 20% 0%, rgba(99,102,241,0.18) 0, transparent 55%), radial-gradient(circle at 95% 30%, rgba(249,115,22,0.12) 0, transparent 65%)" }} />
                <div className="relative">
                  <PackageSelector gameId={id} isBeta={game.isBeta} />
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

export default Product;